'use client'

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { useDynamicI18n } from '@/hooks/use-dynamic-i18n'
import type { Template } from '@/lib/types'
import { formatUrl } from '@/lib/utils'
import { FilesIcon, Layout } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import useSWR from 'swr'

type TTemplatesResponse = {
  data: Template[]
  total: number
}

/**
 * Client component to display similar templates grid with client-side data fetching
 * @returns Similar templates grid, loading state, or empty state
 */
export function SimilarTemplatesGrid() {
  const t = useTranslations('modals.template-preview')

  // Fetch templates client-side for fast initial page load
  const { data: response, isLoading } = useSWR<TTemplatesResponse>(
    '/api/templates?limit=16',
    async (url: string) => {
      const res = await fetch(url)
      const json = await res.json()
      return json
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  const templates = response?.data

  return (
    <section className="mt-16 py-6 border-t border-gray-700">
      <h2 className="text-[28px] sm:text-3xl font-bold text-center mb-10 tracking-tight text-white">
        {t('you_might_also_like_these_templates')}
      </h2>

      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <Skeleton key={i} className="w-full h-[200px] rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && templates && templates.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {templates.map((tmpl) => (
            <TemplateCard key={tmpl._id.toString()} template={tmpl} />
          ))}
        </div>
      )}

      {!isLoading && (!templates || templates.length === 0) && (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FilesIcon />
            </EmptyMedia>
            <EmptyTitle>No Templates Found</EmptyTitle>
            <EmptyDescription>
              There are no similar templates available at this time.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
    </section>
  )
}

/**
 * Individual template card component
 * @param template - Template object to display
 * @returns Template card with link
 */
function TemplateCard({ template }: { template: Template }) {
  const { dt } = useDynamicI18n()

  return (
    <Link
      href={`/template/${template.slug || template._id}`}
      className="w-full text-left group"
    >
      <div className="rounded-xl transition-all duration-200 bg-white/5 border border-white/10 hover:border-white/30 hover:bg-white/10">
        <div className="relative">
          <div className="w-full aspect-video rounded-t-xl flex items-center justify-center overflow-hidden bg-white/5">
            {template.image_url ? (
              <img
                src={template.image_url}
                alt={template.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <Layout className="w-8 h-8 text-white/40" />
            )}
          </div>
        </div>

        <div className="p-4">
          <h3 className="text-[16px] text-white group-hover:text-white/80 transition-colors">
            {dt(template.ai_template_name || template.title)}
          </h3>
        </div>
      </div>
    </Link>
  )
}
